import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { QuestionDialogComponent } from '../components/question-dialog/question-dialog.component';
import { Question } from './models/question.model';
import { QuestionOption } from './models/option.model';
import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root',
})
export class QuestionDataService {
  private questions: Question[] = [
    {
      id: 1,
      question:
        'When should a patient NOT go straight to CT? (Select all that apply)',
      type: 'multiple-choice',
      rationale:
        'Although stroke treatment is time sensitive and minutes matter, the patient must be stable prior to proceeding to diagnostic work-up and treatment. Additionally, patients need to be cooperative to compete a head CT.',
      options: [
        {
          text: 'Need for airway management',
          nextTimeCode: 398,
          isCorrect: true,
        },
        {
          text: 'Hemodynamically unstable',
          nextTimeCode: 398,
          isCorrect: true,
        },
        {
          text: 'Agitated/combative',
          nextTimeCode: 398,
          isCorrect: true,
        },
        {
          text: 'Signs/symptoms consistent with simultaneous acute coronary syndrome',
          nextTimeCode: 398,
          isCorrect: true,
        },
      ],
      timeCode: 398,
      staticCard: true,
      nextQuestionId: 2,
    },
    {
      id: 2,
      question: 'Is the patient appropriate to go straight to CT?',
      type: 'single-choice',
      rationale:
        'During rapid assessment, if the patient is stable based on vital signs, airway, breathing and circulation they should go straight to CT/MRI scanner, if your hospital processes allow. If possible, keep on the ambulance stretcher to get to imaging to save time.',
      options: [
        {
          text: 'Yes',
          nextTimeCode: 398,
          isCorrect: true,
          message:
            'During rapid assessment, if the patient is stable based on vital signs, airway, breathing and circulation they should go straight to CT/MRI scanner, if your hospital processes allow. If possible, keep on the ambulance stretcher to get to imaging to save time.',
        },
        {
          text: 'No',
          nextTimeCode: 398,
          isCorrect: false,
          message:
            'During rapid assessment, if the patient is stable based on vital signs, airway, breathing and circulation they should go straight to CT/MRI scanner, if your hospital processes allow. If possible, keep on the ambulance stretcher to get to imaging to save time.',
        },
      ],
      timeCode: 399,
      staticCard: true,
    },
    {
      id: 3,
      question: 'Should the team start preparing to give thrombolytics?',
      type: 'single-choice',
      rationale:
        'Best practices is to plan on giving thrombolytics until a reason to exclude is known.',
      options: [
        {
          text: 'Yes',
          nextTimeCode: 468,
          isCorrect: true,
        },
        {
          text: 'No',
          nextTimeCode: 468,
          isCorrect: false,
        },
      ],
      timeCode: 468,
      staticCard: true,
      nextQuestionId: 4,
    },
    {
      id: 4,
      question:
        'What would exclude this patient from receiving IV Thrombolytics? ',
      type: 'multiple-choice',
      rationale:
        'Anticoagulants do not automatically exclude patients from receiving thrombolytics. Additional laboratory testing and medication history are needed to determine thrombolytic eligibility for patients on anticoagulation therapy. The clinical practice guidelines state maintaining blood pressure less than 185/110  prior to thrombolytic administration. Administration of thrombolytics for patients with a known head bleed is not recommended as it can increase morbidity and mortality. ​',
      options: [
        {
          text: 'History of anticoagulation',
          nextTimeCode: 468,
          isCorrect: false,
        },
        {
          text: 'Blood pressure of 175/90',
          nextTimeCode: 468,
          isCorrect: false,
        },
        {
          text: 'Head Bleed ',
          nextTimeCode: 468,
          isCorrect: true,
        },
      ],
      timeCode: 469,
      staticCard: true,
    },

    {
      id: 5,
      question: 'Why would we be doing a CTA? ',
      type: 'single-choice',
      rationale:
        'A CTA utilizes contrast dye to produce images of the blood vessels and surrounding tissue. This diagnostic test can confirm if an LVO is present and in combination with the physical exam, helps determine if a patient is a candidate for mechanical thrombectomy.',
      options: [
        {
          text: 'To look for a head bleed',
          nextTimeCode: 570,
          isCorrect: false,
        },
        {
          text: 'To Determine if the patient has a LVO and potentially need a thrombectomy ',
          nextTimeCode: 570,
          isCorrect: true,
        },
        {
          text: 'To see if a candidate for thrombolytics',
          nextTimeCode: 570,
          isCorrect: false,
        },
      ],
      timeCode: 570,
      staticCard: true,
      nextQuestionId: 6,
    },
    {
      id: 6,
      question: 'Why are we suspecting an LVO? Select all that apply.',
      type: 'multiple-choice',
      rationale:
        'Determining if a patient has an LVO is multifactorial. Cortical symptoms, symptoms that arise from an injury to the cerebral cortex and a RACE score ≥ 5 are indicative of a LVO and should include a thorough evaluation. Cortical symptoms can vary depending on the precise location of the stroke. Common cortical symptoms for left hemisphere strokes include right hemiparesis, left gaze preference, aphasia, and a right field cut. Common cortical symptoms of a right hemisphere stroke include left hemiparesis, right gaze preference, neglect, and a left field cut.',
      options: [
        {
          text: 'Presence of left-sided weakness, difficulty speaking and right gaze deviation',
          nextTimeCode: 570,
          isCorrect: true,
        },
        {
          text: 'Race Score of 9',
          nextTimeCode: 570,
          isCorrect: true,
        },
        {
          text: 'Patient is on anticoagulation',
          nextTimeCode: 570,
          isCorrect: false,
        },
      ],
      timeCode: 571,
      staticCard: true,
    },
    {
      id: 7,
      question:
        'What are the blood pressure parameters before administering thrombolytics',
      type: 'single-choice',
      rationale:
        'The clinical practice guidelines for the management of acute ischemic stroke outline maintaining BP < 185/110 following thrombolytic administration. Evidence supports that the risk for hemorrhage following thrombolytic administration increases with elevated BPs. ',
      options: [
        {
          text: '<185/110 mmHg',
          nextTimeCode: 848,
          isCorrect: true,
        },
        {
          text: '<220/120 mmHg',
          nextTimeCode: 848,
          isCorrect: false,
        },
        {
          text: '<140/90 mmHg',
          nextTimeCode: 848,
          isCorrect: false,
        },
      ],
      timeCode: 848,
      staticCard: true,
      nextQuestionId: 8,
    },
    {
      id: 8,
      question:
        'What does the blood pressure need to be maintained at for the next 24 hours after thrombolytic administration:',
      type: 'single-choice',
      rationale:
        'The clinical practice guidelines for the management of acute ischemic stroke outline maintaining BP < 180/105 following thrombolytic administration. Evidence supports that the risk for hemorrhage following thrombolytic administration increases with elevated BPs.',
      options: [
        {
          text: '<185/110 mmHg',
          nextTimeCode: 848,
          isCorrect: false,
        },
        {
          text: '<180/105 mmHg',
          nextTimeCode: 848,
          isCorrect: true,
        },
        {
          text: '<140/90 mmHg',
          nextTimeCode: 848,
          isCorrect: false,
        },
      ],
      timeCode: 849,
      staticCard: true,
      nextQuestionId: 9,
    },
    {
      id: 9,
      question:
        'What is the required monitoring frequency for Vital Signs and Neurologic Assessments when administering thrombolytics?',
      type: 'single-choice',
      rationale:
        'Frequent vital sign monitoring and neurological assessments following thrombolytic administration is critical for optimizing outcomes for stroke patients. Post thrombolytic monitoring is recommended to be completed in an intensive care setting or designated stroke unit. Important to maintain BP < 180/105 and treat with antihypertensive medications as needed. Monitor for signs of neurological deterioration and if such outcome develops, notify the provider immediately. Also important to monitor and assess for angioedema, the development of edema involving the larynx, palate, floor of mouth, or oropharynx with rapid progression,  for post thrombolytic patients.',
      options: [
        {
          text: 'Monitor every hour',
          nextTimeCode: 848,
          isCorrect: false,
        },
        {
          text: 'There are no monitoring requirements',
          nextTimeCode: 848,
          isCorrect: false,
        },
        {
          text: 'Within 15 minutes before administering the thrombolytic bolus, then q15 minute after the bolus x 2 hours, then q30 minutes x 6 hours, then q1 hour for 16 hours, for a total of 24 hours of close monitoring.',
          nextTimeCode: 848,
          isCorrect: true,
        },
      ],
      timeCode: 850,
      staticCard: true,
    },

  ];
  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private router: Router
  ) {}

  getQuestions(): Question[] {
    return this.questions;
  }

  getQuestionById(id: number): Question | undefined {
    return this.questions.find((q) => q.id === id);
  }

  getQuestionAtTime(currentTime: number): Question | null {
    // Find a question that should be displayed at the current time (with a small tolerance)
    const tolerance = 0.5; // half-second tolerance
    return (
      this.questions.find(
        (q) => Math.abs(q.timeCode - currentTime) < tolerance
      ) || null
    );
  }

  openQuestionDialog(question: Question): Promise<QuestionOption> {
    this.sharedService.setModelOpen(true);
    const dialogRef = this.dialog.open(QuestionDialogComponent, {
      width: '500px',
      disableClose: true,
      data: question,
    });

    return dialogRef.afterClosed().toPromise();
  }

  navigateToSuccess(): void {
    this.router.navigate(['/success']);
  }
}
